import React from 'react';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { useAgents } from '../../contexts/AgentContext';
import SafeIcon from '../../common/SafeIcon';
import { FiBarChart2, FiClock, FiEdit, FiLoader, FiMic, FiPause, FiPhone, FiPlay, FiTrash2, FiTrendingUp, FiUsers } from 'react-icons/fi';


const AgentList = ({ agents, viewMode, onEditAgent, loading }) => {
  const { 
    deleteAgent, 
    toggleAgentStatus, 
    testAgent, 
    loading: actionLoading 
  } = useAgents();

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'testing':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getScenarioColor = (scenario) => {
    const colors = {
      'initial_outreach': 'bg-blue-50 text-blue-700',
      'follow_up': 'bg-green-50 text-green-700',
      'meeting_reminder': 'bg-purple-50 text-purple-700',
      'reactivation': 'bg-orange-50 text-orange-700'
    };
    return colors[scenario] || 'bg-gray-50 text-gray-700';
  };

  const getScenarioLabel = (scenario) => {
    const labels = {
      'initial_outreach': 'Initial Outreach',
      'follow_up': 'Follow-up',
      'meeting_reminder': 'Meeting Reminder',
      'reactivation': 'Re-activation'
    };
    return labels[scenario] || scenario;
  };

  const handleTestAgent = async (agentId) => {
    await testAgent(agentId);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
        <SafeIcon icon={FiLoader} className="animate-spin text-blue-600 text-3xl mx-auto mb-4" />
        <p className="text-gray-600">Loading agents...</p>
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
        <SafeIcon icon={FiMic} className="text-gray-300 text-4xl mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No AI agents found</h3>
        <p className="text-gray-500 mb-6">
          Create your first AI agent to start making automated recruitment calls.
        </p>
        <button className="text-blue-600 hover:text-blue-700 font-medium">
          Create Your First Agent
        </button>
      </div>
    );
  }

  if (viewMode === 'list') {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Agent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Scenario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Used
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {agents.map((agent, index) => (
                <motion.tr
                  key={agent.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                        <SafeIcon icon={FiMic} className="text-white text-lg" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {agent.name}
                        </div>
                        <div className="text-sm text-gray-500">{agent.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getScenarioColor(agent.scenario)}`}>
                      {getScenarioLabel(agent.scenario)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(agent.status)}`}>
                      {agent.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="space-y-1">
                      <div>Calls: {agent.stats.totalCalls}</div>
                      <div className="text-xs text-gray-500">
                        Success: {agent.stats.successRate}%
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {agent.lastUsed ? format(new Date(agent.lastUsed), 'MMM d, yyyy') : 'Never'}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleTestAgent(agent.id)}
                        disabled={actionLoading}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors disabled:opacity-50"
                        title="Test agent"
                      >
                        <SafeIcon icon={FiPhone} className="text-sm" />
                      </button>
                      <button
                        onClick={() => toggleAgentStatus(agent.id)}
                        className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                        title={agent.status === 'active' ? 'Deactivate' : 'Activate'}
                      >
                        <SafeIcon icon={agent.status === 'active' ? FiPause : FiPlay} className="text-sm" />
                      </button>
                      <button
                        onClick={() => onEditAgent(agent)}
                        className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
                        title="Edit agent"
                      >
                        <SafeIcon icon={FiEdit} className="text-sm" />
                      </button>
                      <button
                        onClick={() => deleteAgent(agent.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete agent"
                      >
                        <SafeIcon icon={FiTrash2} className="text-sm" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {agents.map((agent, index) => (
        <motion.div
          key={agent.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
        >
          {/* Agent Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiMic} className="text-white text-xl" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-gray-900">{agent.name}</h3>
                <p className="text-sm text-gray-500">{agent.description}</p>
              </div>
            </div>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(agent.status)}`}>
              {agent.status}
            </span>
          </div>

          {/* Agent Details */}
          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Scenario:</span>
              <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getScenarioColor(agent.scenario)}`}>
                {getScenarioLabel(agent.scenario)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Voice:</span>
              <span className="text-sm font-medium text-gray-900 capitalize">{agent.voice}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Target:</span>
              <span className="text-sm text-gray-900">{agent.targetAudience.join(', ')}</span>
            </div>
          </div>

          {/* Performance Stats */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-lg font-bold text-gray-900">{agent.stats.totalCalls}</p>
                <p className="text-xs text-gray-500">Total Calls</p>
              </div>
              <div>
                <p className="text-lg font-bold text-green-600">{agent.stats.successRate}%</p>
                <p className="text-xs text-gray-500">Success Rate</p>
              </div>
              <div>
                <p className="text-lg font-bold text-blue-600">{agent.stats.avgDuration}</p>
                <p className="text-xs text-gray-500">Avg Duration</p>
              </div>
            </div>
          </div>

          {/* Last Used */}
          <div className="flex items-center text-sm text-gray-500 mb-4">
            <SafeIcon icon={FiClock} className="mr-2" />
            Last used: {agent.lastUsed ? format(new Date(agent.lastUsed), 'MMM d, yyyy') : 'Never'}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleTestAgent(agent.id)}
                disabled={actionLoading}
                className="flex items-center gap-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50 transition-colors"
              >
                <SafeIcon icon={FiPhone} className="text-xs" />
                Test
              </button>
              <button className="flex items-center gap-1 px-3 py-1 text-sm text-purple-600 hover:text-purple-700 transition-colors">
                <SafeIcon icon={FiBarChart2} className="text-xs" />
                Analytics
              </button>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => toggleAgentStatus(agent.id)}
                className={`p-1 rounded transition-colors ${
                  agent.status === 'active' 
                    ? 'text-orange-600 hover:text-orange-700' 
                    : 'text-green-600 hover:text-green-700'
                }`}
                title={agent.status === 'active' ? 'Deactivate' : 'Activate'}
              >
                <SafeIcon icon={agent.status === 'active' ? FiPause : FiPlay} className="text-sm" />
              </button>
              <button
                onClick={() => onEditAgent(agent)}
                className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
                title="Edit agent"
              >
                <SafeIcon icon={FiEdit} className="text-sm" />
              </button>
              <button
                onClick={() => deleteAgent(agent.id)}
                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                title="Delete agent"
              >
                <SafeIcon icon={FiTrash2} className="text-sm" />
              </button>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default AgentList;